import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, Eye, EyeOff, Delete, AlertTriangle, Store } from "lucide-react";
import "./Login.css";

export default function Login() {
  const { login, loginWithPin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [storeUsername, setStoreUsername] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState<"email" | "pin">("email");
  const [error, setError] = useState("");
  const [trialExpiredMsg, setTrialExpiredMsg] = useState(false);
  const [storeDisabledMsg, setStoreDisabledMsg] = useState(false);

  const handleEmailLogin = async () => {
    if (!email || !password) { setError("Please enter email and password"); return; }
    setTrialExpiredMsg(false);
    setStoreDisabledMsg(false);
    const result = await login(email, password);
    if (result.success) {
      const stored = localStorage.getItem("auth_user");
      const isSys = stored && JSON.parse(stored).isSysAdmin;
      if (isSys) {
        navigate("/system-admin");
      } else {
        navigate("/dashboard");
      }
    } else if (result.error === "trial_expired") {
      setTrialExpiredMsg(true);
      setError("");
    } else if (result.error === "store_disabled") {
      setStoreDisabledMsg(true);
      setError("");
    } else {
      setError("Invalid email or password.");
    }
  };

  const handlePinDigit = async (digit: string) => {
    if (!storeUsername.trim()) {
      setError("Enter your store username first");
      return;
    }
    const newPin = pin + digit;
    if (newPin.length <= 4) {
      setPin(newPin);
      setError("");
      if (newPin.length === 4) {
        const ok = await loginWithPin(newPin, storeUsername.trim());
        if (ok) navigate("/dashboard");
        else { setError("Invalid PIN or store username. Contact your store admin."); setPin(""); }
      }
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <Sparkles size={32} />
          </div>
          <h1>Showroom AiPro</h1>
          <p>AI-Powered Sales Platform</p>
        </div>

        {trialExpiredMsg && (
          <div className="trial-expired-banner">
            <AlertTriangle size={18} />
            <div>
              <strong>Your 7-day free trial has ended.</strong>
              <p>Subscribe to continue using Showroom AiPro at $99/month.</p>
            </div>
          </div>
        )}

        {storeDisabledMsg && (
          <div className="trial-expired-banner">
            <AlertTriangle size={18} />
            <div>
              <strong>Your account has been deactivated.</strong>
              <p>Please contact Showroom AiPro support for assistance.</p>
            </div>
          </div>
        )}

        <div className="login-tabs">
          <button className={`login-tab ${mode === "email" ? "active" : ""}`} onClick={() => { setMode("email"); setError(""); }}>Email Login</button>
          <button className={`login-tab ${mode === "pin" ? "active" : ""}`} onClick={() => { setMode("pin"); setError(""); setPin(""); }}>Quick PIN</button>
        </div>

        {mode === "email" ? (
          <div className="login-form">
            <div className="form-group">
              <label>Email or Username</label>
              <input type="text" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@yourstore.com or username"
                onKeyDown={(e) => e.key === "Enter" && handleEmailLogin()} />
            </div>
            <div className="form-group">
              <label>Password</label>
              <div className="password-field">
                <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password"
                  onKeyDown={(e) => e.key === "Enter" && handleEmailLogin()} />
                <button className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button className="btn btn-primary login-submit" onClick={handleEmailLogin}>Sign In</button>
            <div style={{ textAlign: "center", marginTop: 12 }}>
              <button
                type="button"
                className="forgot-password-link"
                onClick={() => navigate("/forgot-password")}
                style={{ background: "none", border: "none", color: "#1B6EF3", cursor: "pointer", fontSize: 13, textDecoration: "underline" }}
              >
                Forgot your password?
              </button>
            </div>
          </div>
        ) : (
          <div className="pin-login">
            <div className="pin-store-field">
              <label><Store size={14} /> Store Username</label>
              <input
                type="text"
                value={storeUsername}
                onChange={(e) => { setStoreUsername(e.target.value); setError(""); }}
                placeholder="Enter your store's username"
                autoComplete="off"
              />
            </div>
            <div className="pin-dots">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className={`pin-dot ${i < pin.length ? "filled" : ""}`} />
              ))}
            </div>
            <p className="pin-label">Enter your 4-digit PIN</p>
            <div className="pin-pad">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                <button key={n} className="pin-key" onClick={() => handlePinDigit(String(n))}>{n}</button>
              ))}
              <div />
              <button className="pin-key" onClick={() => handlePinDigit("0")}>0</button>
              <button className="pin-key pin-delete" onClick={() => { setPin(pin.slice(0, -1)); setError(""); }}>
                <Delete size={20} />
              </button>
            </div>
          </div>
        )}

        {error && <p className="login-error">{error}</p>}
      </div>
    </div>
  );
}
